import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, timer, EMPTY } from 'rxjs';
import {
  switchMap,
  takeUntil,
  tap,
  catchError,
  distinctUntilChanged
} from 'rxjs/operators';
import {
  ExportStatus,
  ExportStatusEnum,
  ExportButtonState,
  ExportButtonStateEnum,
  ReportsResponse
} from '../interfaces/export-status.interface';
import { environment } from '@environments';
import { Utils } from '@serasa/shared/utils/utils';
import { SedsSnackbarService, SedsSnackbarButtonAction } from "@serasa/shared/seds";

@Injectable({
  providedIn: 'root',
})
export class ExportPollingService implements OnDestroy {
  private readonly POLLING_INTERVAL = 30 * 1000;
  private readonly API_URL = `${environment.logonPortalBaseUrl}/my-reports`;

  private buttonStateSubject = new BehaviorSubject<ExportButtonState>({
    buttonState: ExportButtonStateEnum.REQUEST_EXPORT,
    disabled: false,
  });

  private stopPolling$ = new Subject<void>();

  private destroy$ = new Subject<void>();

  private isPollingActive = false;

  private currentReportId: string | null = null;

  private hasShownDownloadNotification = false;

  public buttonState$: Observable<ExportButtonState> = this.buttonStateSubject
    .asObservable()
    .pipe(
      distinctUntilChanged(
        (prev, curr) =>
          prev.buttonState === curr.buttonState &&
          prev.disabled === curr.disabled
      )
    );

  constructor(
    private http: HttpClient,
    private snackbarService: SedsSnackbarService
  ) {}

  /**
   * Verifica o status inicial do export quando o usuário entra na tela
   * Só inicia o polling se houver arquivo em processamento
   */
  public checkInitialStatus(): void {
    this.getReports()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (!response || !response.reports || response.reports.length === 0) {
            this.updateButtonState({
              buttonState: ExportButtonStateEnum.REQUEST_EXPORT,
              disabled: false,
            });
            this.currentReportId = null;
            return;
          }
          const latestReport = response.reports[0];
          const status: ExportStatus = {
            reportId: latestReport.reportId,
            status: latestReport.status,
            updatedDate: latestReport.updatedDate,
          };

          this.currentReportId = latestReport.reportId;
          this.handleExportStatus(status);

          if (latestReport.status === ExportStatusEnum.PROCESSING) {
            this.startPolling();
          }
        },
        error: () => {
          this.updateButtonState({
            buttonState: ExportButtonStateEnum.REQUEST_EXPORT,
            disabled: false,
          });
          this.currentReportId = null;
        },
      });
  }

  /**
   * Inicia o processo de exportação
   * Chamado quando o usuário clica em "Solicitar exportação"
   */
  public requestExport(): Observable<any> {
    this.hasShownDownloadNotification = false;
    
    this.updateButtonState({
      buttonState: ExportButtonStateEnum.IN_PROCESSING,
      disabled: true,
    });

    const headers = Utils.generateDefaultHeader();
    return this.http.post(`${this.API_URL}/generate`, {}, { headers }).pipe(
      takeUntil(this.destroy$),
      tap(() => {
        this.startPolling();
      }),
      catchError(() => {
        this.updateButtonState({
          buttonState: ExportButtonStateEnum.REQUEST_EXPORT,
          disabled: false,
        });
        return EMPTY;
      })
    );
  }

  /**
   * Inicia o polling para verificar o status do arquivo
   * Verifica a cada 5 minutos
   */
  private startPolling(): void {
    if (this.isPollingActive) {
      return;
    }

    this.stopPolling$ = new Subject<void>();
    this.isPollingActive = true;

    timer(0, this.POLLING_INTERVAL)
      .pipe(
        takeUntil(this.stopPolling$),
        takeUntil(this.destroy$),
        switchMap(() => this.getReports()),
        tap((response) => {
          if (response && response.reports && response.reports.length > 0) {
            const latestReport = response.reports[0];
            const status: ExportStatus = {
              reportId: latestReport.reportId,
              status: latestReport.status,
              updatedDate: latestReport.updatedDate,
            };
            this.currentReportId = latestReport.reportId;
            this.handleExportStatus(status);
          }
        }),
        catchError(() => {
          return EMPTY;
        })
      )
      .subscribe();
  }

  public stopPolling(): void {
    if (this.isPollingActive) {
      this.stopPolling$.next();
      this.stopPolling$.complete();
      this.isPollingActive = false;
    }
  }

  private getReports(): Observable<ReportsResponse> {
    const headers = Utils.generateDefaultHeader();
    return this.http.get<ReportsResponse>(this.API_URL, { headers });
  }

  private handleExportStatus(status: ExportStatus): void {
    if (!status.status) {
      this.updateButtonState({
        buttonState: ExportButtonStateEnum.REQUEST_EXPORT,
        disabled: false,
      });
      return;
    }

    switch (status.status) {
      case ExportStatusEnum.PROCESSING:
        this.updateButtonState({
          buttonState: ExportButtonStateEnum.IN_PROCESSING,
          disabled: true,
        });
        break;

      case ExportStatusEnum.REPORT_GENERATED:
        if (status.reportId) {
          this.updateButtonState({
            buttonState: ExportButtonStateEnum.DOWNLOAD_READY,
            disabled: false,
            reportId: status.reportId,
          });
          
          if (!this.hasShownDownloadNotification) {
            this.showNotification(
              'A lista de acessos está disponível para ser baixada.',
              'success'
            );
            this.hasShownDownloadNotification = true;
          }
        } else {
          this.updateButtonState({
            buttonState: ExportButtonStateEnum.REQUEST_EXPORT,
            disabled: false,
          });
        }
        this.stopPolling();
        break;

      case ExportStatusEnum.FAILED:
        this.updateButtonState({
          buttonState: ExportButtonStateEnum.REQUEST_EXPORT,
          disabled: false,
        });
        this.showNotification(
          'Não foi possível baixar a lista!',
          'error'
        );
        this.stopPolling();
        break;

      case ExportStatusEnum.DOWNLOADED:
      default:
        this.updateButtonState({
          buttonState: ExportButtonStateEnum.REQUEST_EXPORT,
          disabled: false,
        });
        this.stopPolling();
        break;
    }
  }

  private updateButtonState(state: ExportButtonState): void {
    this.buttonStateSubject.next(state);
  }

  /**
   * Faz o download do arquivo e marca como baixado
   * Chamado quando o usuário clica no botão de download
   * @returns Observable que completa quando o download inicia
   */
  public downloadFile(): Observable<void> {
    if (!this.currentReportId) {
      return EMPTY;
    }

    const downloadUrl = `${this.API_URL}/${this.currentReportId}/download`;
    const headers = Utils.generateDefaultHeader();

    return this.http.get(downloadUrl, { 
      headers,
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      takeUntil(this.destroy$),
      tap((response) => {
        const blob = response.body as Blob;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `report_${this.currentReportId}.xlsx`;
        
        if (contentDisposition) {
          const matches = /filename="?([^;"]+)"?/.exec(contentDisposition);
          if (matches && matches[1]) {
            filename = matches[1];
          }
        }

        filename = filename.replace(/[^a-zA-Z0-9_.-]/g, '_');
        
        link.download = filename;
        link.click();
        
        window.URL.revokeObjectURL(url);
        
        this.markAsDownloaded();
        this.showNotification(
          'Lista baixada com sucesso!',
          'success'
        );
      }),
      catchError(() => {
        this.showNotification(
          'Não foi possível baixar a lista!',
          'error'
        );
        return EMPTY;
      }),
      switchMap(() => new Observable<void>((observer) => {
        observer.next();
        observer.complete();
      }))
    );
  }

  /**
   * Marca o arquivo como baixado e reseta o estado
   * Chamado após o usuário fazer o download
   */
  public markAsDownloaded(): void {
    this.updateButtonState({
      buttonState: ExportButtonStateEnum.REQUEST_EXPORT,
      disabled: false,
    });
    this.currentReportId = null;
    this.hasShownDownloadNotification = false;
    this.stopPolling();
  }

  /**
   * Retorna o estado atual do botão (síncrono)
   */
  public getCurrentButtonState(): ExportButtonState {
    return this.buttonStateSubject.getValue();
  }

  /**
   * Limpa recursos quando o serviço é destruído
   * Previne vazamento de memória
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopPolling$.complete();
    this.buttonStateSubject.complete();
    this.isPollingActive = false;
  }

  /**
   * Método para limpar o estado manualmente (quando o usuário sai da tela)
   */
  public cleanup(): void {
    this.stopPolling();
    this.currentReportId = null;
  }

  /**
   * Exibe notificação usando SedsSnackbarService
   */
  private showNotification(message: string, type: 'success' | 'error' | 'info'): void {
    const baseOptions: any = {
      duration: 5000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      typeButton: SedsSnackbarButtonAction.CLOSE,
    };

    if (type === 'success') {
      this.snackbarService.open(message, {
        ...baseOptions,
        iconLeft: 'check_circle',
      });
    } else if (type === 'error') {
      this.snackbarService.open(message, {
        ...baseOptions,
        iconLeft: 'error',
      });
    } else {
      this.snackbarService.open(message, {
        ...baseOptions,
        iconLeft: 'info',
      });
    }
  }
}
